import { z } from 'zod';
import { CoSellMsftCreateFormDataId as DataId } from '../constants';
import type { FieldConfigType } from './types';
import { ReferralFormSchema } from '../referralFormSchema';

const CustomerMarketplaceIntentOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
  { label: 'Have not decided', value: 'HaveNotDecided' },
] as const;

export enum FieldEnum {
  name = 'name',
  mpnId = 'mpnId',
  dealValue = 'details.dealValue',
  currency = 'details.currency',
  closingDateTime = 'details.closingDateTime',
  crmId = 'crmId',
  campaignId = 'campaignId',
  notes = 'details.notes',
  customerMarketplaceIntent = 'details.requirements.customerMarketplaceIntent',
  solutionArea = 'details.requirements.solutionArea',
  solutionPlay = 'details.requirements.solutionPlay',
}

export const FieldMap: Record<FieldEnum, FieldConfigType> = {
  [FieldEnum.name]: {
    label: 'Deal name',
    required: true,
    type: 'text',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-name`,
  },
  [FieldEnum.mpnId]: {
    label: 'Deal location',
    required: true,
    type: 'single-select',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-mpnId`,
  },
  [FieldEnum.dealValue]: {
    label: 'Estimated value',
    required: true,
    type: 'number',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-value`,
    helperText:
      "Increase acceptance rates by up to 15% with accurate deal values over Microsoft's minimum of $25,000.00.",
  },
  [FieldEnum.currency]: {
    label: 'Currency',
    required: true,
    type: 'single-select',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-currency`,
  },
  [FieldEnum.closingDateTime]: {
    label: 'Estimated close date',
    required: true,
    type: 'date',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-close-date`,
  },
  [FieldEnum.crmId]: {
    label: 'CRM ID',
    required: false,
    type: 'text',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-crm-id`,
  },
  [FieldEnum.campaignId]: {
    label: 'Marketing campaign ID',
    required: false,
    type: 'text',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-campaign-id`,
  },
  [FieldEnum.notes]: {
    label: 'Customer need',
    required: false,
    type: 'textarea',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-notes`,
    helperText:
      "Describe the business problem that most closely aligns to your customer's use case.",
    placeholder:
      'Example - The customer needs a basic disaster recovery solution, or the customer acquired a new organization and needs 1,000 seats more of M354 E3. Avoid adding any personally identifiable information.',
  },
  [FieldEnum.customerMarketplaceIntent]: {
    label: 'Customer purchase intent',
    required: true,
    type: 'radio-group',
    options: [...CustomerMarketplaceIntentOptions],
    dataId: `${DataId.FIELD_PREFIX}-deal-details-customer-marketplace-intent`,
    helperText: 'Selecting “Yes” increases the likelihood of deal acceptance.',
  },
  [FieldEnum.solutionArea]: {
    label: 'Solution area',
    required: true,
    type: 'single-select',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-solution-area`,
  },
  [FieldEnum.solutionPlay]: {
    label: 'Solution play',
    required: true,
    type: 'single-select',
    dataId: `${DataId.FIELD_PREFIX}-deal-details-solution-play`,
  },
};

/** Deal details section schemas */

const BaseItems = ReferralFormSchema.pick({
  campaignId: true,
  crmId: true,
  mpnId: true,
  name: true,
});

const Requirements = ReferralFormSchema.shape.details.shape.requirements.pick({
  customerMarketplaceIntent: true,
  solutionArea: true,
  solutionPlay: true,
});

const Details = ReferralFormSchema.shape.details
  .pick({
    closingDateTime: true,
    currency: true,
    dealValue: true,
    notes: true,
  })
  .extend({
    requirements: Requirements,
  });

export const DealDetailsSectionSchema = BaseItems.extend({
  details: Details,
});

export type DealDetailsFields = z.infer<typeof DealDetailsSectionSchema>;
